import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const wholesaleSellersApi = createApi({
  reducerPath: "wholesaleSellersApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WholesaleSeller", "WholesaleSellerStats"],
  endpoints: (build) => ({
    // Get all sellers
    getAllSellers: build.query({
      query: ({ status, motivation, leadSource, search, page = 1, limit = 100, starred } = {}) => ({
        url: "/wholesale/sellers",
        method: "GET",
        params: { status, motivation, leadSource, search, page, limit, starred },
      }),
      providesTags: ["WholesaleSeller"],
    }),

    // Get seller by ID
    getSellerById: build.query({
      query: (id) => ({
        url: `/wholesale/sellers/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "WholesaleSeller", id }],
    }),

    // Create seller
    createSeller: build.mutation({
      query: (seller) => ({
        url: "/wholesale/sellers",
        method: "POST",
        body: seller,
      }),
      invalidatesTags: ["WholesaleSeller", "WholesaleSellerStats"],
    }),

    // Update seller
    updateSeller: build.mutation({
      query: ({ id, ...seller }) => ({
        url: `/wholesale/sellers/${id}`,
        method: "PUT",
        body: seller,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleSeller", id },
        "WholesaleSeller",
        "WholesaleSellerStats",
      ],
    }),

    // Delete seller
    deleteSeller: build.mutation({
      query: (id) => ({
        url: `/wholesale/sellers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WholesaleSeller", "WholesaleSellerStats"],
    }),

    // Update seller status
    updateSellerStatus: build.mutation({
      query: ({ id, status }) => ({
        url: `/wholesale/sellers/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleSeller", id },
        "WholesaleSeller",
        "WholesaleSellerStats",
      ],
    }),

    // Toggle star
    toggleSellerStar: build.mutation({
      query: (id) => ({
        url: `/wholesale/sellers/${id}/star`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "WholesaleSeller", id },
        "WholesaleSeller",
      ],
    }),

    // Get seller stats
    getSellerStats: build.query({
      query: () => ({
        url: "/wholesale/sellers/stats",
        method: "GET",
      }),
      providesTags: ["WholesaleSellerStats"],
    }),

    // Convert seller to deal
    convertSellerToDeal: build.mutation({
      query: (id) => ({
        url: `/wholesale/sellers/${id}/convert-to-deal`,
        method: "POST",
      }),
      invalidatesTags: ["WholesaleSeller", "WholesaleSellerStats", "WholesaleDeal"],
    }),

    // Unlink deal from seller (undo convert)
    unlinkDeal: build.mutation({
      query: ({ id, deleteDeal = true }) => ({
        url: `/wholesale/sellers/${id}/unlink-deal?deleteDeal=${deleteDeal}`,
        method: "PATCH",
      }),
      invalidatesTags: ["WholesaleSeller", "WholesaleSellerStats", "WholesaleDeal"],
    }),

    // Log contact attempt
    // Accepts a bare id (logs the touch) or { id, followUpDate } to log the
    // touch and book the next one in a single request. Pass followUpDate: null
    // to clear it.
    logContact: build.mutation({
      query: (arg) => {
        const id = typeof arg === "object" && arg !== null ? arg.id : arg;
        const hasFollowUp = typeof arg === "object" && arg !== null && "followUpDate" in arg;
        return {
          url: `/wholesale/sellers/${id}/log-contact`,
          method: "PATCH",
          ...(hasFollowUp ? { body: { followUpDate: arg.followUpDate } } : {}),
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "WholesaleSeller", id: typeof arg === "object" && arg !== null ? arg.id : arg },
        "WholesaleSeller",
        "WholesaleSellerStats",
      ],
    }),
  }),
});

export const {
  useGetAllSellersQuery,
  useGetSellerByIdQuery,
  useCreateSellerMutation,
  useUpdateSellerMutation,
  useDeleteSellerMutation,
  useUpdateSellerStatusMutation,
  useToggleSellerStarMutation,
  useGetSellerStatsQuery,
  useConvertSellerToDealMutation,
  useUnlinkDealMutation,
  useLogContactMutation,
} = wholesaleSellersApi;
